import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";
import { isa } from "@p0security/util/types/is";

import {
  DATABRICKS_AI_CONNECTOR_FUNCTION_NAME,
  DatabricksAccountsUrls,
} from "./constants.ts";

// copy of UUID_PATTERN in packages/integrations/entra-id/shared/src/azure-ad/types.ts
const UUID_PATTERN =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

// copy of uuidValidator in packages/integrations/databricks/shared/src/components.ts
/**
 * Refuses a value that isn't a UUID, naming what it is (`label`). The
 * connector parses account and application IDs with zod's `guid()`, which
 * takes the same shape.
 */
const newUuidValidator =
  (label: string) => async (_id: string, value: string) => {
    if (!UUID_PATTERN.test(value))
      return `${label} are UUIDs, e.g. 01234567-89ab-cdef-0123-456789abcdef.`;
  };

// copy of toUrl in packages/integrations/databricks/shared/src/components.ts
// `URL.parse` would do this, but the frontend bundles this file, and its
// browser targets (`build.target` in frontend/vite.config.mjs) predate it.
const toUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

/** Whether `url` is an origin alone, with or without a trailing slash. */
const isBareOrigin = (url: URL) =>
  url.pathname === "/" &&
  !url.search &&
  !url.hash &&
  !url.username &&
  !url.password;

/**
 * The accounts URL to save for `value`: its origin when `value` is an origin
 * alone, so `https://accounts.cloud.databricks.com/` matches
 * `DatabricksAccountsUrls`. Any other value comes back as it is, for the
 * validator to refuse.
 */
export const toAccountsUrl = (value: string) => {
  const url = toUrl(value);
  return url && isBareOrigin(url) ? url.origin : value;
};

/**
 * One install, two rungs: `connector` (whose key is the `aws` item) →
 * `account`, which points at its connector with a `dynamic` element the
 * customer chooses. Workspaces and catalogs aren't installed: each request
 * names its own, and the connector finds them through the account.
 *
 * The item keys are external identifiers: an AWS account ID and a Databricks
 * account ID (a UUID). A chosen pointer's options come from
 * `integrationOptionProvider`, which hides any candidate whose key is also a
 * key of the component being installed, so each component's keys must not
 * coincide with those of the component it points at.
 */
export const DatabricksAiComponents = InstallSpec({
  connector: ItemComponent({
    description: `The ${DATABRICKS_AI_CONNECTOR_FUNCTION_NAME} Lambda that P0 invokes to manage Databricks, one per AWS account. Its key is the ID of an AWS account installed in P0's AWS integration. Every Databricks account below is reached through it.`,
    label: "Connector",
    maturity: "beta",
    schema: {
      region: Element({
        description: "The AWS region the connector's Lambda runs in",
        label: "Region",
        step: "new",
        type: "dynamic",
      }),
    },
    type: "dynamic",
  }),
  account: ItemComponent({
    description:
      "One Databricks account, which P0 manages through a service principal there that the connector federates into. The principal is account admin, and metastore admin on every metastore in the account. P0 also makes it workspace admin of each workspace an agent lists or requests, and of the lowest-ID workspace when the account is saved. P0 never undoes it. Its key is the Databricks account ID.",
    label: "Account",
    maturity: "beta",
    // In the order the install form shows them.
    schema: {
      connector: Element({
        description: "The installed connector that reaches this account",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      accountsUrl: Element({
        description:
          "The URL of this account's console, https://accounts.cloud.databricks.com. P0 supports Databricks on AWS only.",
        label: "Accounts URL",
        step: "new",
        type: "string",
        validator: async (_id, value) => {
          const url = toUrl(value);
          if (
            !url ||
            !isBareOrigin(url) ||
            !isa(DatabricksAccountsUrls, url.origin)
          )
            return `The accounts URL must be the AWS account console, ${DatabricksAccountsUrls.join(", ")}. P0 supports Databricks on AWS only.`;
        },
      }),
      // An input, not a discovery: the connector needs it to authenticate at
      // all, as the client ID of its federated token exchange. A configure
      // field, because the account's Terraform creates the principal only
      // after the item is added.
      applicationId: Element({
        description:
          "The application ID of the service principal this account's Terraform creates, from its output. The connector authenticates to Databricks as this principal.",
        label: "Service principal application ID",
        step: "configure",
        type: "string",
        validator: newUuidValidator("Application IDs"),
      }),
      ownerPrincipal: Element({
        description:
          "Who owns what an agent creates once its access ends: a group's display name, a user's userName, or a service principal's application ID. P0 hands each table, view, volume and function an agent created to it before deleting the agent's service principal.",
        label: "Owner principal",
        step: "configure",
        type: "string",
      }),
      // Set by the backend each time the account is saved, never entered.
      // Its value is "true" or "false", since a hidden element holds a string.
      unityGatewayBeta: Element({
        description:
          "Whether the account's Unity Gateway beta was on when the account was last saved. The Databricks SQL MCP server needs it.",
        hidden: true,
        label: "Unity Gateway beta",
        optional: true,
        type: "hidden",
      }),
    },
    type: "string",
    validator: newUuidValidator("Databricks account IDs"),
  }),
});
